import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { ArrowRight } from '@outdoor-voices/ui';
import cc from 'classcat';
import PropTypes from 'prop-types';
import React, { memo, useRef, useState } from 'react';
import { EarnRewards } from '../../../components/EarnRewards';
import { getTitle, scrollToReviews } from '../utils';
import { INSEAM_PRODUCT_IDS } from '../constants';
import { TEXT } from '../../../config/constants-sizes';
import AddToBag from '../AddToBag';
import ButtonsSwatches from '../../../components/ButtonsSwatches';
import ColorwayBadge from '../../../components/ColorwayBadge';
import css from './index.module.scss';
import InseamDropdown from '../InseamDropdown';
import Rating from '../../../components/Rating';
import SelectAdvanced from '../../../components/SelectAdvanced';
import Shipping from '../Shipping';
import SizeGuideOverlay from '../../../components/SizeGuideOverlay';
import stripHtmlTags from '../../../utils/strings/stripHtmlTags';
import WishListCache from '../../customers/WishListCache';
import WishListItemToggle from '../../pages/wish-lists/WishListItemToggle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var SHAKE_CLASS = 'shake-animation';
var _TEXT$fit = TEXT.fit,
    sizeDownCopy = _TEXT$fit.sizeDown,
    sizeUpCopy = _TEXT$fit.sizeUp;

var Label = function Label(_ref) {
  var className = _ref.className,
      badge = _ref.badge,
      title = _ref.title,
      value = _ref.value;
  return /*#__PURE__*/_jsxs("div", {
    className: cc([css.label, className]),
    children: [title.length > 0 && "".concat(title, ":"), /*#__PURE__*/_jsx("span", {
      className: css.labelValue,
      children: value
    }), badge]
  });
};

Label.defaultProps = {
  className: '',
  badge: false,
  value: ''
};
Label.propTypes = {
  className: PropTypes.string,
  badge: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  title: PropTypes.string.isRequired,
  value: PropTypes.string
};

var SideBar = function SideBar(_ref2) {
  var addToBag = _ref2.addToBag,
      available = _ref2.available,
      color = _ref2.color,
      colorwayId = _ref2.colorwayId,
      comingSoon = _ref2.comingSoon,
      compareAtPrice = _ref2.compareAtPrice,
      currentSize = _ref2.currentSize,
      displayRating = _ref2.displayRating,
      displayWaitlist = _ref2.displayWaitlist,
      finalSale = _ref2.finalSale,
      onColorSelect = _ref2.onColorSelect,
      onSizeSelect = _ref2.onSizeSelect,
      oneSize = _ref2.oneSize,
      price = _ref2.price,
      productId = _ref2.productId,
      rating = _ref2.rating,
      relatedCta = _ref2.relatedCta,
      relatedUrl = _ref2.relatedUrl,
      reviewable = _ref2.reviewable,
      reviewCount = _ref2.reviewCount,
      shopifyId = _ref2.shopifyId,
      showWaitlist = _ref2.showWaitlist,
      sizeDown = _ref2.sizeDown,
      sizeGuide = _ref2.sizeGuide,
      sizes = _ref2.sizes,
      sizeSelected = _ref2.sizeSelected,
      sizeUp = _ref2.sizeUp,
      styledWith = _ref2.styledWith,
      submitReviewURL = _ref2.submitReviewURL,
      submitting = _ref2.submitting,
      subTitle = _ref2.subTitle,
      swatches = _ref2.swatches,
      swatchNum = _ref2.swatchNum,
      title = _ref2.title,
      urgencyMessage = _ref2.urgencyMessage,
      variant = _ref2.variant,
      waitlistTypeDisplay = _ref2.waitlistTypeDisplay;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      adding = _useState2[0],
      setAdding = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      showSelectSize = _useState4[0],
      setShowSizeSelect = _useState4[1];

  var errorMessageRef = useRef(null);
  var selectSizePromptRef = useRef(null);

  var onAddToBag = function onAddToBag(event) {
    // A size has been selected, just call add to bag
    if (sizeSelected || oneSize) {
      if (event && event.preventDefault) {
        event.preventDefault();
      }

      addToBag();
      return;
    } // Toggle size shake on adding


    toggleAdding();
  };

  var handleSizeSelect = function handleSizeSelect(newSize) {
    onSizeSelect(newSize);
    setAdding(false);
    setShowSizeSelect(false);
  };

  var getSizeSelectNode = function getSizeSelectNode() {
    var shake = adding && !sizeSelected;
    return /*#__PURE__*/_jsx("div", {
      className: cc([css.select, _defineProperty({}, SHAKE_CLASS, shake)]),
      children: /*#__PURE__*/_jsx(SelectAdvanced, {
        compact: true,
        createPortal: true,
        id: "pdp-select-size",
        itemDisabledLabel: waitlistTypeDisplay,
        label: "Select your size",
        onChange: handleSizeSelect,
        options: sizes,
        value: sizeSelected ? currentSize : ''
      })
    });
  };

  var getSwatches = function getSwatches() {
    if (!swatches) {
      return false;
    }

    return /*#__PURE__*/_jsx(ButtonsSwatches, {
      currentSize: sizeSelected ? currentSize : '',
      enableClickWhenDisabled: true,
      expand: true,
      label: "Select a color",
      onClick: onColorSelect,
      options: swatches,
      sizeSelected: sizeSelected,
      value: colorwayId
    });
  };

  var getRatingButton = function getRatingButton() {
    if (!displayRating) {
      return /*#__PURE__*/_jsx("a", {
        className: css.writeReview,
        href: submitReviewURL,
        children: "Write the first review"
      });
    }

    return /*#__PURE__*/_jsxs("a", {
      className: css.ratingButton,
      onClick: scrollToReviews,
      href: "#reviews",
      children: [/*#__PURE__*/_jsx("div", {
        className: css.ratingButtonRating,
        children: /*#__PURE__*/_jsx(Rating, {
          count: 5,
          value: rating,
          size: 13
        })
      }), /*#__PURE__*/_jsx("div", {
        className: css.ratingButtonCount,
        "aria-label": "".concat(reviewCount, " total reviews."),
        children: reviewCount
      }), /*#__PURE__*/_jsx("div", {
        className: css.ratingButtonViewAll,
        children: "View All Reviews"
      })]
    });
  };

  var toggleAdding = function toggleAdding() {
    setTimeout(function () {
      errorMessageRef.current.focus();
    }, 150);

    if (adding) {
      return;
    }

    setAdding(true);
    setShowSizeSelect(true);
  };

  var openSelect = function openSelect() {
    document.querySelector('#pdp-select-size-button').click();
  };

  var addToBagProps = {
    available: available,
    compareAtPrice: compareAtPrice,
    comingSoon: comingSoon,
    displayWaitlist: displayWaitlist,
    finalSale: finalSale,
    onAddToBag: onAddToBag,
    onShowWaitlist: showWaitlist,
    price: price,
    sizeSelected: sizeSelected,
    shopifyId: variant.shopifyId,
    submitting: submitting,
    title: title
  };

  var productBadge = /*#__PURE__*/_jsx(ColorwayBadge, {
    compareAtPrice: compareAtPrice,
    inline: true,
    price: price
  });

  var hasSizeGuide = sizeGuide !== 'NONE';
  var sizingCopy = '';

  if (sizeUp) {
    sizingCopy = sizeUpCopy;
  }

  if (sizeDown) {
    sizingCopy = sizeDownCopy;
  }

  var cache = new WishListCache().get();
  var cachedWishlistItem = false;

  if (cache && cache.colorways[colorwayId]) {
    cachedWishlistItem = true;
  }

  return /*#__PURE__*/_jsx("div", {
    className: css.root,
    children: /*#__PURE__*/_jsxs("div", {
      className: css.rootInner,
      children: [/*#__PURE__*/_jsxs("div", {
        className: css.titleWrap,
        children: [/*#__PURE__*/_jsxs("h1", {
          className: css.title,
          itemProp: "name",
          children: [getTitle(shopifyId, title), /*#__PURE__*/_jsx(WishListItemToggle, {
            colorwayColor: color,
            colorwayId: colorwayId,
            initialFilledIn: cachedWishlistItem,
            productId: productId,
            productTitle: title
          }, colorwayId)]
        }), reviewable && getRatingButton(), subTitle && /*#__PURE__*/_jsx("p", {
          className: css.description,
          dangerouslySetInnerHTML: {
            __html: stripHtmlTags(subTitle)
          }
        }), relatedCta && relatedUrl && /*#__PURE__*/_jsxs("a", {
          className: css.relatedCta,
          href: relatedUrl,
          children: [relatedCta, " ", /*#__PURE__*/_jsx(ArrowRight, {})]
        })]
      }), swatchNum > 0 && /*#__PURE__*/_jsxs("div", {
        className: css.row,
        children: [/*#__PURE__*/_jsxs("div", {
          className: css.miniWrap,
          children: [productBadge && /*#__PURE__*/_jsx(Label, {
            title: "Color",
            value: color,
            badge: productBadge
          }), !!urgencyMessage && /*#__PURE__*/_jsxs("span", {
            className: cc([css.label, css.urgencyMessage]),
            children: ["\xA0 \u2013 \xA0", urgencyMessage]
          })]
        }), swatches && /*#__PURE__*/_jsx("div", {
          className: css.miniWrap,
          children: /*#__PURE__*/_jsx("div", {
            className: css.swatches,
            children: getSwatches()
          })
        })]
      }), sizes && /*#__PURE__*/_jsxs(_Fragment, {
        children: [sizes.length > 1 && /*#__PURE__*/_jsxs("div", {
          className: cc([css.row, css.rowSizes]),
          ref: selectSizePromptRef,
          children: [INSEAM_PRODUCT_IDS.includes(shopifyId) && /*#__PURE__*/_jsx(InseamDropdown, {
            productId: shopifyId
          }), /*#__PURE__*/_jsxs("div", {
            className: css.sizeHeader,
            children: [/*#__PURE__*/_jsx(Label, {
              title: "Size",
              value: sizingCopy
            }), hasSizeGuide && /*#__PURE__*/_jsx(SizeGuideOverlay, {
              type: sizeGuide
            })]
          }), getSizeSelectNode()]
        }), /*#__PURE__*/_jsx("div", {
          className: css.selectSizeMessage,
          role: "status",
          children: /*#__PURE__*/_jsx("button", {
            "aria-hidden": showSelectSize ? undefined : true,
            className: cc([css.inner, _defineProperty({}, css.show, showSelectSize)]),
            ref: errorMessageRef,
            type: "button",
            onClick: openSelect,
            tabIndex: showSelectSize ? '0' : '-1',
            children: "Select a Size"
          })
        })]
      }), /*#__PURE__*/_jsx(EarnRewards, {
        price: price
      }), /*#__PURE__*/_jsx(AddToBag, _objectSpread({}, addToBagProps)), /*#__PURE__*/_jsx(Shipping, {
        padded: !styledWith
      }), styledWith && /*#__PURE__*/_jsx("div", {
        className: css.styledWith,
        children: styledWith
      })]
    })
  });
};

SideBar.defaultProps = {
  comingSoon: false,
  compareAtPrice: 0,
  customDetailDrawer: false,
  details: false,
  displayRating: false,
  displayWaitlist: true,
  finalSale: false,
  rating: false,
  reviewable: false,
  reviewCount: 0,
  submitReviewURL: '',
  subTitle: false,
  swatchNum: 0,
  urgencyMessage: ''
};
SideBar.propTypes = {
  addToBag: PropTypes.func.isRequired,
  available: PropTypes.bool.isRequired,
  color: PropTypes.string.isRequired,
  colorwayId: PropTypes.string.isRequired,
  comingSoon: PropTypes.bool,
  compareAtPrice: PropTypes.number,
  currentSize: PropTypes.string.isRequired,
  displayRating: PropTypes.bool,
  displayWaitlist: PropTypes.bool,
  finalSale: PropTypes.bool,
  inventoryQuantity: PropTypes.number.isRequired,
  onColorSelect: PropTypes.func.isRequired,
  oneSize: PropTypes.bool.isRequired,
  onSizeSelect: PropTypes.func.isRequired,
  price: PropTypes.number.isRequired,
  productId: PropTypes.string.isRequired,
  rating: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  relatedCta: PropTypes.string,
  relatedUrl: PropTypes.string,
  reviewable: PropTypes.bool,
  reviewCount: PropTypes.number,
  shopifyId: PropTypes.number.isRequired,
  showWaitlist: PropTypes.func.isRequired,
  sizeDown: PropTypes.bool.isRequired,
  sizeDropdown: PropTypes.bool.isRequired,
  sizeGuide: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  sizes: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  sizeSelected: PropTypes.bool.isRequired,
  sizeUp: PropTypes.bool.isRequired,
  styledWith: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  submitReviewURL: PropTypes.string,
  submitting: PropTypes.bool.isRequired,
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  swatches: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]).isRequired,
  swatchNum: PropTypes.number,
  title: PropTypes.string.isRequired,
  urgencyMessage: PropTypes.string,
  variant: PropTypes.shape().isRequired,
  waitlistTypeDisplay: PropTypes.string.isRequired
};
export default /*#__PURE__*/memo(SideBar);